import { useCallback, useRef, useEffect } from "react";

/**
 * Hook customizado para debounce de funções
 * @param callback - Função a ser debounced
 * @param delay - Delay em milissegundos
 * @returns Função debounced
 */
export const useDebounce = <T extends (...args: any[]) => any>(
  callback: T,
  delay: number,
): T => {
  const timeoutRef = useRef<NodeJS.Timeout>();
  const callbackRef = useRef(callback);

  // Atualiza a ref do callback sempre que ele mudar
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // Cleanup do timeout quando o componente for desmontado
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return useCallback(
    ((...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        if (callbackRef.current) {
          callbackRef.current(...args);
        }
      }, delay);
    }) as T,
    [delay], // Remove callback da dependência
  );
};

/**
 * Hook para debounce de funções assíncronas que retornam Promise
 * @param callback - Função assíncrona a ser debounced
 * @param delay - Delay em milissegundos
 * @returns Função debounced que retorna Promise
 */
export const useAsyncDebounce = <T extends (...args: any[]) => Promise<any>>(
  callback: T,
  delay: number,
): T => {
  const timeoutRef = useRef<NodeJS.Timeout>();
  const promiseRef = useRef<Promise<any> | null>(null);
  const latestArgsRef = useRef<Parameters<T> | null>(null);
  const callbackRef = useRef(callback);

  // Atualiza a ref do callback sempre que ele mudar
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // Cleanup do timeout quando o componente for desmontado
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return useCallback(
    ((...args: Parameters<T>) => {
      // Armazena os argumentos mais recentes
      latestArgsRef.current = args;

      // Se já existe uma Promise em andamento, retorna ela
      if (promiseRef.current) {
        return promiseRef.current;
      }

      // Cria uma nova Promise
      promiseRef.current = new Promise((resolve, reject) => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(async () => {
          try {
            // Usa os argumentos mais recentes
            const latestArgs = latestArgsRef.current;
            if (!latestArgs) {
              reject(new Error("No arguments available"));
              return;
            }

            if (callbackRef.current) {
              const result = await callbackRef.current(...latestArgs);
              resolve(result);
            } else {
              reject(new Error("Callback is not available"));
            }
          } catch (error) {
            reject(error);
          } finally {
            promiseRef.current = null;
            latestArgsRef.current = null;
          }
        }, delay);
      });

      return promiseRef.current;
    }) as T,
    [delay], // Remove callback da dependência
  );
};

/**
 * Hook específico para debounce de criação de sessões
 * @param createSessionFn - Função de criação de sessão
 * @returns Função de criação de sessão debounced
 */
export const useSessionDebounce = (
  createSessionFn: (documentId: string) => Promise<any>,
) => {
  return useAsyncDebounce(createSessionFn, 300); // 300ms de delay
};
